// Web Speech API Diktat-Button — de-AT, browserNativ, kostenfrei
// Graceful degrade wenn nicht unterstützt

import React, { useState, useRef, useEffect } from 'react';

// Web Speech API Typen (webkit-prefixed, nicht in allen TS-Lib-Versionen)
interface ISpeechRecognition extends EventTarget {
  lang:             string;
  continuous:       boolean;
  interimResults:   boolean;
  maxAlternatives:  number;
  onstart:          (() => void) | null;
  onend:            (() => void) | null;
  onerror:          ((evt: { error: string }) => void) | null;
  onresult:         ((evt: { resultIndex: number; results: { length: number; [i: number]: { [j: number]: { transcript: string } | undefined; isFinal?: boolean } | undefined } }) => void) | null;
  start:            () => void;
  stop:             () => void;
  abort:            () => void;
}

function getSpeechRecognition(): (new () => ISpeechRecognition) | null {
  const w = window as unknown as Record<string, unknown>;
  const API = w['SpeechRecognition'] ?? w['webkitSpeechRecognition'];
  return (API as (new () => ISpeechRecognition) | undefined) ?? null;
}

interface SpeechToTextButtonProps {
  onTranscript: (text: string) => void;
  language?:    string;
  disabled?:    boolean;
  className?:   string;
}

export function SpeechToTextButton({
  onTranscript,
  language = 'de-AT',
  disabled = false,
  className = '',
}: SpeechToTextButtonProps): React.JSX.Element | null {
  const [isListening, setIsListening] = useState(false);
  const [error,       setError]       = useState('');
  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const supported = getSpeechRecognition() !== null;

  useEffect(() => {
    const API = getSpeechRecognition();
    if (!API) { return; }

    const rec = new API();
    rec.lang             = language;
    rec.continuous       = false;
    rec.interimResults   = false;
    rec.maxAlternatives  = 1;

    rec.onstart  = (): void => { setIsListening(true); setError(''); };
    rec.onend    = (): void => { setIsListening(false); };
    rec.onerror  = (evt): void => {
      setIsListening(false);
      if (evt.error === 'not-allowed') {
        setError('Mikrofon-Zugriff verweigert');
      } else if (evt.error !== 'no-speech') {
        setError(`Fehler: ${evt.error}`);
      }
    };
    rec.onresult = (evt): void => {
      let transcript = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        transcript += evt.results[i]?.[0]?.transcript ?? '';
      }
      if (transcript.trim()) {
        onTranscript(transcript.trim());
      }
    };

    recognitionRef.current = rec;
    return () => { rec.abort(); };
  }, [language, onTranscript]);

  if (!supported) { return null; }

  const handleClick = (): void => {
    const rec = recognitionRef.current;
    if (!rec) { return; }
    if (isListening) {
      rec.stop();
    } else {
      setError('');
      try { rec.start(); } catch { /* bereits aktiv */ }
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={isListening ? 'Aufnahme stoppen' : 'Diktieren (de-AT)'}
        className={`p-2 rounded-lg transition-all flex-shrink-0 ${
          isListening
            ? 'bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400 animate-pulse ring-2 ring-red-400'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950/30 dark:hover:text-blue-400'
        } disabled:opacity-40 ${className}`}
      >
        🎤
      </button>
      {error !== '' && (
        <span className="text-xs text-red-500 whitespace-nowrap">{error}</span>
      )}
    </div>
  );
}

// Wrapper: kombiniert Textarea + SpeechToTextButton
interface SpeechTextareaProps {
  id?:          string;
  value:        string;
  onChange:     (val: string) => void;
  placeholder?: string;
  rows?:        number;
  className?:   string;
  language?:    string;
}

export function SpeechTextarea({
  id, value, onChange, placeholder, rows = 4, className = '', language,
}: SpeechTextareaProps): React.JSX.Element {
  const handleTranscript = (text: string): void => {
    onChange(value ? `${value} ${text}` : text);
  };

  return (
    <div className="flex gap-2 items-start">
      <textarea
        id={id}
        value={value}
        onChange={e => { onChange(e.target.value); }}
        placeholder={placeholder}
        rows={rows}
        className={`input-field flex-1 resize-y ${className}`}
      />
      <SpeechToTextButton onTranscript={handleTranscript} {...(language !== undefined ? { language } : {})} />
    </div>
  );
}
